import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HTTP_INTERCEPTORS } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { catchError, Observable, switchMap, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { GTM } from '../_services/gtm.service';
import { CiamService } from '../_services/ciam.service';
import { SessionNoticeService } from '../_services/session-notice.service';
import { StorageService } from '../_services/storage.service';

@Injectable()
export class HttpRequestInterceptor implements HttpInterceptor {
  constructor(
    private readonly gtm: GTM,
    private readonly injector: Injector,
    private readonly storageService: StorageService,
    private readonly sessionNotice: SessionNoticeService,
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (req.url.includes('connect/token')) {
      this.gtm.trackInteraction('connexion', 'succes');
    }

    const request = this.addBearerToken(req, this.storageService.getAccessToken());
    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        if (!this.shouldRefresh(request, error)) {
          return throwError(() => error);
        }

        const ciamService = this.injector.get(CiamService);
        return ciamService.refreshTokens().pipe(
          // On n'intercepte que l'échec du ré-échange : une erreur de l'appel rejoué appartient
          // au composant appelant, pas à la gestion de session.
          catchError(refreshError => {
            if (ciamService.isDefinitiveRefreshRejection(refreshError)) {
              // Contrairement au cas « navigation » traité par l'AuthGuard, l'utilisateur est
              // déjà sur une page et resterait devant un écran inerte.
              this.sessionNotice.notifyExpiredSession();
            }

            return throwError(() => refreshError);
          }),
          switchMap(tokens => next.handle(this.addBearerToken(req, tokens.accessToken))),
        );
      }),
    );
  }

  private addBearerToken(req: HttpRequest<any>, accessToken?: string): HttpRequest<any> {
    if (
      !environment.ciam.enabled
      || !accessToken
      || req.headers.has('Authorization')
      || !this.isBackendRequest(req.url)
      || this.isCiamSessionRequest(req.url)
    ) {
      return req;
    }

    return req.clone({
      setHeaders: {
        Authorization: `Bearer ${accessToken}`,
      },
    });
  }

  private shouldRefresh(req: HttpRequest<any>, error: HttpErrorResponse): boolean {
    return environment.ciam.enabled
      && error.status === 401
      && this.isBackendRequest(req.url)
      && !this.isCiamSessionRequest(req.url)
      && Boolean(this.storageService.getRefreshToken());
  }

  private isBackendRequest(url: string): boolean {
    if (url.startsWith('/api/')) {
      return true;
    }

    const backendBaseUrl = environment.apis.default.url.replace(/\/+$/, '');
    return url.startsWith(`${backendBaseUrl}/api/`);
  }

  private isCiamSessionRequest(url: string): boolean {
    return [
      environment.ciam.bff.exchangePath,
      environment.ciam.bff.refreshPath,
      environment.ciam.bff.logoutPath,
    ].some(path => url.includes(path));
  }
}

export const httpInterceptorProviders = [
  { provide: HTTP_INTERCEPTORS, useClass: HttpRequestInterceptor, multi: true },
];
